import React, { useState } from 'react';

export type DateRangePreset =
  | 'today'
  | 'this_week'
  | 'this_month'
  | '3_months'
  | '6_months'
  | '12_months';

export interface DateRange {
  fromDate: Date;
  toDate: Date;
}

interface DateRangeFilterProps {
  defaultPreset?: DateRangePreset;
  onChange: (preset: DateRangePreset, range: DateRange) => void;
}

const presets: { key: DateRangePreset; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'this_week', label: 'This Week' },
  { key: 'this_month', label: 'This Month' },
  { key: '3_months', label: '3 Months' },
  { key: '6_months', label: '6 Months' },
  { key: '12_months', label: '12 Months' },
];

export const computeRange = (preset: DateRangePreset): DateRange => {
  const now = new Date();
  const toDate = new Date(now);
  toDate.setHours(23, 59, 59, 999);

  const fromDate = new Date(now);

  switch (preset) {
    case 'today':
      fromDate.setHours(0, 0, 0, 0);
      break;
    case 'this_week':
      fromDate.setDate(fromDate.getDate() - 6);
      fromDate.setHours(0, 0, 0, 0);
      break;
    case 'this_month':
      fromDate.setDate(1);
      fromDate.setHours(0, 0, 0, 0);
      break;
    case '3_months':
      fromDate.setMonth(fromDate.getMonth() - 3);
      fromDate.setHours(0, 0, 0, 0);
      break;
    case '6_months':
      fromDate.setMonth(fromDate.getMonth() - 6);
      fromDate.setHours(0, 0, 0, 0);
      break;
    case '12_months':
      fromDate.setMonth(fromDate.getMonth() - 12);
      fromDate.setHours(0, 0, 0, 0);
      break;
  }

  return { fromDate, toDate };
};

export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  defaultPreset = '6_months',
  onChange,
}) => {
  const [active, setActive] = useState<DateRangePreset>(defaultPreset);

  const handleClick = (preset: DateRangePreset) => {
    setActive(preset);
    const range = computeRange(preset);
    onChange(preset, range);
  };

  return (
    <div
      style={{
        display: 'inline-flex',
        background: 'var(--neutral-100)',
        border: '1px solid var(--neutral-300)',
        borderRadius: 'var(--radius-md)',
        padding: 4,
        gap: 2,
        flexWrap: 'wrap',
      }}
    >
      {presets.map((p) => {
        const isActive = active === p.key;
        return (
          <button
            key={p.key}
            onClick={() => handleClick(p.key)}
            style={{
              padding: '6px 14px',
              fontSize: 13,
              fontWeight: 500,
              fontFamily: 'var(--font-family)',
              borderRadius: 'var(--radius-sm)',
              background: isActive ? 'var(--blue-600)' : 'transparent',
              color: isActive ? 'var(--neutral-100)' : 'var(--neutral-700)',
              transition: 'all 150ms ease',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => {
              if (!isActive) {
                e.currentTarget.style.background = 'var(--neutral-200)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive) {
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            {p.label}
          </button>
        );
      })}
    </div>
  );
};